// `extension.state.tabs.config.ng1.ts`'s `initIsRTL` / `getIsRTL`, ported.
//
// ⚠️ "RTL" HERE IS NOT A LANGUAGE, IT IS WHICH EDGE THE PANEL HANGS OFF. `getIsRTL` is
// `ShortListAttachTo.Left !== handleLocation.attachTo` and nothing else: drag the handle to
// the right-hand edge of an ATS page and the header's controls mirror. The name is upstream's.
//
// ⚠️ AND IT IS WHAT HIDES THE PIN. `_shortlist-header.scss:3` is
// `.woo-extension-header-view.handle-right-aligned .shortlist-header-ctas .pin-cta { display:
// none }` — so a panel attached right shows no pin at all, which is what production shows
// anyone whose handle sits on the right. Without this hook `isRTL` was a prop nobody passed,
// stuck at `false`, and the pin was drawn in the injected panel where production has none.
//
// Outside a frame there is no parent to ask, `request` rejects at once rather than waiting out
// its timeout (see `IFrameClient`), and `false` stands — which is also what the standalone
// `/chrome-extension/**` tabs want, pin included, and what the baseline captures photograph.
import { useEffect, useState } from 'react'
import { IFrameEventType, ShortListAttachTo, iframeClient, isInIFrame } from '@woo-ext/protocol'

interface HandleLocationPayload {
  handleLocation?: { attachTo?: string }
}

interface DraggedPayload {
  isDragging?: boolean
  handleLocation?: { attachTo?: string }
}

export function useIsRtl(): boolean {
  const [isRTL, setIsRTL] = useState(false)

  useEffect(() => {
    if (!isInIFrame()) return
    const client = iframeClient()
    let live = true

    // Both, in parallel, as upstream's `$q.all` does: the stored location wins and the default
    // fills in a missing `attachTo` rather than being asked for only when the first is absent.
    void Promise.all([
      client.request<HandleLocationPayload>(IFrameEventType.GetShortListDefaultHandleLocation),
      client.request<HandleLocationPayload>(IFrameEventType.GetShortListHandleLocation),
    ]).then(
      ([fallback, stored]) => {
        if (!live) return
        const attachTo = stored.handleLocation?.attachTo ?? fallback.handleLocation?.attachTo
        setIsRTL(attachTo !== undefined && attachTo !== ShortListAttachTo.Left)
      },
      () => {
        // An older extension that does not know these events answers nothing. Upstream has no
        // catch at all and leaves `isRTL` false; this says so out loud and does the same.
      },
    )

    // `initParentMessageListeners` — the handle can move while the frame is open, and only the
    // END of a drag counts (`!isDragging && !!handleLocation`).
    const dispose = client.onMessage((message) => {
      if (message.type !== IFrameEventType.ShortlistDragged) return
      const data = message.data as DraggedPayload | undefined
      if (data?.isDragging !== false || data.handleLocation === undefined) return
      setIsRTL(data.handleLocation.attachTo !== ShortListAttachTo.Left)
    })

    return () => {
      live = false
      dispose()
    }
  }, [])

  return isRTL
}
